import React from 'react';
import { Definition, Property } from '../../../data/Definition';
import { ChartProvider } from '../ChartProvider';
import BarForm from './BarForm';
import { DwQuery, IDwFilter } from '../../../data/Query';
import DataFrame from 'dataframe-js';
import DefaultChartLayout from '../DefPlotLayout';
import { getPlotDataProvider, IPlotProviderData } from '../../../data/dataProviders';
import { isEntity, isTimestampProperty } from '../../../data/utils';
import moment from 'moment';
import { BarChartConfigManager } from './BarChartConfigManager';
import { ChartConfigManager } from '../ChartConfigManager';

export class BarChartProvider extends ChartProvider {
    private onParentSaveConfig: any = null;

    constructor() {
        super('Bar');

        this.onSaveConfig = this.onSaveConfig.bind(this);
    }

    getConfigForm(definition: Definition, chartConfig: any, onSaveConfig: any) {
        this.onParentSaveConfig = onSaveConfig;

        return (
            <BarForm definition={definition} chartConfig={chartConfig} saveConfig={this.onSaveConfig}></BarForm>
        );
    }

    configManager(definition: Definition) : ChartConfigManager {
        return new BarChartConfigManager(definition);
    }

    getQuery(definition: Definition, chartConfig: any): null | DwQuery {
        const configManager = new BarChartConfigManager(definition);

        if (!configManager.validate(chartConfig)) {
            chartConfig = configManager.convert(configManager);
        }

        return super.getQuery(definition, chartConfig);
    }

    filterFromSelection(definition: Definition, chartConfig: any, selection: any): IDwFilter[] {
        let newFilter: IDwFilter;

        const dimensionItem = chartConfig.dimension.source.item;

        // determine the dimension of the chart. if entity, use the id,
        //
        if (isEntity(dimensionItem)) {
            // create the list of ids for the selected entities.
            //
            let ids = selection.points.map((param: any) => {
                return parseInt(param.customdata); // will be the entity id
            });

            newFilter = {
                type: "entity",
                id: this.getPath(definition, dimensionItem),
                name: "",
                operation: "in",
                value: ids
            }
        } else { // property
            const property = dimensionItem as Property;

            // time
            //
            if (isTimestampProperty(property)) {
                // get dates selected.
                //
                const dates = selection.points.map((param: any) => {
                    return moment(param.customdata);
                });

                // get min and max of selected dates.
                //
                const maxDate = moment.max(dates);
                const minDate = moment.min(dates);

                console.log(maxDate, minDate);

                // handle the bin type
                let values: any[] = [];
                if (chartConfig.dimension.bin === 'year') {
                    values = [minDate.clone().startOf('year'), maxDate.clone().endOf('year')];
                } else if (chartConfig.dimension.bin === 'month,year') {
                    values = [minDate.clone().startOf('month'), maxDate.clone().endOf('month')];
                } else if (chartConfig.dimension.bin === 'year,week') {
                    values = [minDate.clone().startOf('day'), maxDate.clone().add(6, 'days').endOf('day')];
                } else if (chartConfig.dimension.bin === 'date') {
                    values = [minDate.clone().startOf('day'), maxDate.clone().endOf('day')];
                }

                // the dates will be bin-ed by year, month, day
                //
                newFilter = {
                    type: "property",
                    id: this.getPath(definition, property),
                    name: property.property,
                    operation: "between",
                    value: [values[0].format('YYYY-MM-DD HH:mm:ss'), values[1].format('YYYY-MM-DD HH:mm:ss')]
                }
            } else { // string
                let ids = selection.points.map((param: any) => {
                    return param.customdata;
                });

                newFilter = {
                    type: "property",
                    id: this.getPath(definition, property),
                    name: property.property,
                    operation: "in",
                    value: ids
                }
            }
        }

        return [newFilter];
    }

    getPlotly(definition: Definition, chartConfig: any, data: any): {} {
        // use the default layout and update the items for the given chart type.
        // make a deep copy.
        //
        let layout: any = JSON.parse(JSON.stringify(DefaultChartLayout));

        if (!data || data.length === 0) {
            return {
                data: [],
                layout: layout
            };
        }

        // put the data into a data frame so we can standardize how
        // to interact with the rows.
        //
        //console.log("data", data);
        const df = this.dataToDataFrame(data);
        //console.log("df", df);
        // get the data provider for dimension.
        //
        const dataProvider = getPlotDataProvider(chartConfig.dimension, chartConfig.measure, df);
        const plotData = dataProvider.getData();

        //
        let traces: any[] = [];

        traces.push({
            type: 'bar',
            x: plotData.map((d: IPlotProviderData) => d.x),
            y: plotData.map((d: IPlotProviderData) => d.y),
            customdata: plotData.map((d: IPlotProviderData) => d.meta)
        });

        layout.xaxis.title = dataProvider.dimensionTitle();
        layout.yaxis.title = dataProvider.measureTitle();

        const {yRange} = chartConfig;
        if(yRange && yRange.min && yRange.max) {
            layout.yaxis.range = [yRange.min, yRange.max];
            layout.yaxis.autorange = false;
        }
        
        if (isTimestampProperty(chartConfig.dimension.source.item)) {
            layout.xaxis.type = 'data';

            if (chartConfig.dimension.bin === 'year') {
                layout.xaxis.tickformat = '%Y';
            } else if (chartConfig.dimension.bin === 'month,year') {
                layout.xaxis.tickformat = '%B %Y';
            } else if (chartConfig.dimension.bin === 'date') {

            }
        }

        return {
            data: traces,
            layout: layout
        };
    }

    dataToDataFrame(data: any) {
        let rows: any[] = [];
        data.forEach((d: any) => {
            let row: any[] = [];

            Object.keys(d).forEach((key: any) => {
                row.push(d[key]);
            });

            rows.push(row);
        });

        let header: string[] = [];
        Object.keys(data[0]).forEach((key: any) => {
            header.push(key);
        });

        return new DataFrame(rows, header);
    }

    onSaveConfig(chartConfig: any) {
        this.onParentSaveConfig(chartConfig);
    }
}
